import * as Sentry from "@sentry/react";
import { GlobalErrorBoundary } from "./components/GlobalErrorBoundary";

Sentry.init({
  dsn: "https://8aef06fb459c263739621bcad71a0f4d@o4509732386504704.ingest.us.sentry.io/4509732407410688",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration()
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.manageclean\.com\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// TEST 2: Re-enable ClerkProvider
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
// TEST 3: Re-enable ConvexProvider
import { ConvexReactClient } from "convex/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";

// TEST 4: Re-enable AuthContext
import { AuthProvider } from "./contexts/AuthContext";

import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import "./styles/proposal.css";

// PostHog Configuration
import { PostHogProvider } from "@posthog/react";

const postHogOptions = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: "2026-01-30",
} as const;

// TEST 3: Re-enable Convex client
// Explicitly enable verbose logging to ensure logger is initialized
const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string, {
  verbose: false, // Set to true for debugging
});

const basename = import.meta.env.BASE_URL;

// TEST 2: Re-enable Clerk configuration
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key");
}

// Main application render
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={postHogOptions}>
      <GlobalErrorBoundary>
        <HelmetProvider>
          <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
            <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
              <AuthProvider>
                <BrowserRouter basename={basename}>
                  <App />
                </BrowserRouter>
              </AuthProvider>
            </ConvexProviderWithClerk>
          </ClerkProvider>
        </HelmetProvider>
      </GlobalErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>
);
